import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { formatMoney } from '../../utils/formatNumber';
import './AggregatedView.css';

/**
 * Компонент для отображения агрегированных WB данных за месяц
 */
export function WbAggregatedView({ accountId }) {
  const { request, showStatus } = useApi();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');

  useEffect(() => {
    loadAvailableMonths();
  }, [accountId]);

  const loadAvailableMonths = async () => {
    try {
      const response = await request(`/api/v1/tax-reports/wb/${accountId}/weeks`);
      // Извлекаем уникальные месяцы из недель
      const weeks = response.weeks || [];
      const monthsSet = new Set();

      weeks.forEach(week => {
        // week формат: "YYYY-MM-DD - YYYY-MM-DD"
        const match = week.match(/^(\d{4}-\d{2})/);
        if (match) {
          monthsSet.add(match[1]);
        }
      });

      const months = Array.from(monthsSet).sort().reverse();
      setAvailableMonths(months);

      if (months.length > 0) {
        setSelectedMonth(months[0]);
        loadMonthData(months[0]);
      }
    } catch (error) {
      showStatus('error', 'Не удалось загрузить список периодов');
    }
  };

  const loadMonthData = async (month) => {
    if (!month) return;

    setLoading(true);
    try {
      const monthData = await request(`/api/v1/tax-reports/wb/${accountId}/summary/${month}`);
      setData(monthData);
    } catch (error) {
      showStatus('error', `Не удалось загрузить данные за ${month}`);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    loadMonthData(month);
  };


  return (
    <div className="aggregated-view">
      <h3>Агрегированный отчёт по месяцам</h3>

      <div style={{ marginBottom: '20px' }}>
        <label>
          Выберите месяц:
          <select
            value={selectedMonth}
            onChange={(e) => handleMonthChange(e.target.value)}
            style={{ marginLeft: '10px', padding: '8px', fontSize: '14px' }}
          >
            <option value="">-- Выберите месяц --</option>
            {availableMonths.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </label>
      </div>

      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Загрузка данных...</p>
        </div>
      )}

      {!loading && data && data.totals && (
        <>
          {/* Итого за месяц */}
          <div className="totals-card">
            <h4>Итого за {data.base_month}</h4>
            <div className="totals-grid">
              <div className="total-item total-income">
                <div className="total-label">Выручка</div>
                <div className="total-value">{formatMoney(data.totals.retail_amount)}</div>
              </div>
              <div className="total-item total-expenses">
                <div className="total-label">Комиссия</div>
                <div className="total-value">{formatMoney(data.totals.ppvz_sales_commission)}</div>
              </div>
              <div className="total-item total-net">
                <div className="total-label">К перечислению</div>
                <div className="total-value">{formatMoney(data.totals.ppvz_for_pay)}</div>
              </div>
              <div className="total-item total-credits">
                <div className="total-label">Платежи по кредитам</div>
                <div className="total-value">{formatMoney(data.totals.credit_payments)}</div>
              </div>
            </div>

            <div className="totals-stats">
              <span>Недель: <strong>{data.weeks?.length || 0}</strong></span>
            </div>
          </div>

          {/* Детализация по неделям */}
          {data.weeks && data.weeks.length > 0 && (
            <div className="monthly-breakdown">
              <h4>Детализация по неделям</h4>
              <table className="monthly-table">
                <thead>
                  <tr>
                    <th>Неделя</th>
                    <th className="align-right">Выручка</th>
                    <th className="align-right">Комиссия</th>
                    <th className="align-right">К перечислению</th>
                    <th className="align-right">Кредиты</th>
                    <th className="align-center">Строк</th>
                  </tr>
                </thead>
                <tbody>
                  {data.weeks.map(week => (
                    <tr key={week.week_period}>
                      <td className="month-cell">{week.week_period}</td>
                      <td className="align-right income-cell">{formatMoney(week.retail_amount)}</td>
                      <td className="align-right expenses-cell">{formatMoney(week.ppvz_sales_commission)}</td>
                      <td className={`align-right net-cell ${week.ppvz_for_pay >= 0 ? 'positive' : 'negative'}`}>
                        {formatMoney(week.ppvz_for_pay)}
                      </td>
                      <td className="align-right">{formatMoney(week.credit_payments)}</td>
                      <td className="align-center">{week.total_rows}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="totals-row">
                    <td><strong>ИТОГО:</strong></td>
                    <td className="align-right"><strong>{formatMoney(data.totals.retail_amount)}</strong></td>
                    <td className="align-right"><strong>{formatMoney(data.totals.ppvz_sales_commission)}</strong></td>
                    <td className={`align-right ${data.totals.ppvz_for_pay >= 0 ? 'positive' : 'negative'}`}>
                      <strong>{formatMoney(data.totals.ppvz_for_pay)}</strong>
                    </td>
                    <td className="align-right"><strong>{formatMoney(data.totals.credit_payments)}</strong></td>
                    <td className="align-center"><strong>{data.totals.total_rows}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
